import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Select, TimePicker} from 'antd';
import dayjs from 'dayjs';
import React, {useRef} from 'react';

const PollingScheduleEditor = ({
    value,
    onChange,
    disabled,
    dayOptions,
    addLabel,
    addDayLabel,
    removeLabel,
    removeDayLabel,
    showValidation,
    timePlaceholder,
}) => {
    const itemId = useRef(0);
    const emptyTimeKey = useRef();
    const timeKeys = useRef([]);
    const groupKeys = useRef(new Map());

    const createKey = (prefix) => `${prefix}-${itemId.current++}`;
    const createEmptyTime = () => ({key: createKey('time'), hour: null, minute: null});
    const getDefaultDay = () => dayOptions?.[0]?.value ?? 1;
    const getGroupKey = (dayOfWeek) => {
        if (!groupKeys.current.has(dayOfWeek)) {
            groupKeys.current.set(dayOfWeek, createKey('day'));
        }

        return groupKeys.current.get(dayOfWeek);
    };

    const getGroups = () => {
        if (!Array.isArray(value) || value.length === 0) {
            timeKeys.current = [];
            emptyTimeKey.current ??= createKey('time');

            return [
                {
                    key: getGroupKey(getDefaultDay()),
                    dayOfWeek: getDefaultDay(),
                    times: [{key: emptyTimeKey.current, hour: null, minute: null}],
                },
            ];
        }

        const groups = [];

        value.forEach((entry, entryIndex) => {
            let group = groups.find((item) => item.dayOfWeek === entry.dayOfWeek);

            if (!group) {
                group = {
                    key: getGroupKey(entry.dayOfWeek),
                    dayOfWeek: entry.dayOfWeek,
                    times: [],
                };

                groups.push(group);
            }

            const timeKey = timeKeys.current[entryIndex] ?? createKey('time');

            timeKeys.current[entryIndex] = timeKey;
            group.times.push({
                key: timeKey,
                hour: entry.hour,
                minute: entry.minute,
            });
        });

        return groups;
    };

    const groups = getGroups();

    const emitGroups = (updatedGroups) => {
        const entries = updatedGroups.flatMap((group) =>
            group.times.map((time) => ({
                key: time.key,
                dayOfWeek: group.dayOfWeek,
                hour: time.hour,
                minute: time.minute,
            }))
        );

        timeKeys.current = entries.map((entry) => entry.key);
        onChange?.(
            entries.map(({dayOfWeek, hour, minute}) => ({
                dayOfWeek,
                hour,
                minute,
            }))
        );
    };

    const updateDay = (groupIndex, dayOfWeek) => {
        const currentGroup = groups[groupIndex];

        if (currentGroup.dayOfWeek !== dayOfWeek) {
            groupKeys.current.delete(currentGroup.dayOfWeek);
            groupKeys.current.set(dayOfWeek, currentGroup.key);
        }

        emitGroups(groups.map((group, index) => (index === groupIndex ? {...group, dayOfWeek} : group)));
    };

    const addDay = () => {
        const usedDays = new Set(groups.map((group) => group.dayOfWeek));
        const nextDay = dayOptions?.find((option) => !usedDays.has(option.value));

        if (!nextDay) return;

        emitGroups([
            ...groups,
            {
                key: getGroupKey(nextDay.value),
                dayOfWeek: nextDay.value,
                times: [createEmptyTime()],
            },
        ]);
    };

    const removeDay = (groupIndex) => {
        groupKeys.current.delete(groups[groupIndex].dayOfWeek);
        emitGroups(groups.filter((_, index) => index !== groupIndex));
    };

    const addTime = (groupIndex) => {
        emitGroups(
            groups.map((group, index) =>
                index === groupIndex ? {...group, times: [...group.times, createEmptyTime()]} : group
            )
        );
    };

    const updateTime = (groupIndex, timeIndex, selectedTime) => {
        emitGroups(
            groups.map((group, index) => {
                if (index !== groupIndex) return group;

                return {
                    ...group,
                    times: group.times.map((time, index) =>
                        index === timeIndex
                            ? {
                                  ...time,
                                  hour: selectedTime?.hour() ?? null,
                                  minute: selectedTime?.minute() ?? null,
                              }
                            : time
                    ),
                };
            })
        );
    };

    const removeTime = (groupIndex, timeIndex) => {
        emitGroups(
            groups.map((group, index) => {
                if (index !== groupIndex) return group;

                return {
                    ...group,
                    times: group.times.filter((_, index) => index !== timeIndex),
                };
            })
        );
    };

    const usedDays = new Set(groups.map((group) => group.dayOfWeek));
    const canAddDay = dayOptions?.some((option) => !usedDays.has(option.value)) ?? false;
    const timeCounts = groups.reduce((counts, group) => {
        group.times.forEach(({hour, minute}) => {
            const isValidTime =
                Number.isInteger(hour) &&
                hour >= 0 &&
                hour <= 23 &&
                Number.isInteger(minute) &&
                minute >= 0 &&
                minute <= 59;

            if (!isValidTime) return;

            const key = `${group.dayOfWeek}-${hour}-${minute}`;

            counts.set(key, (counts.get(key) ?? 0) + 1);
        });

        return counts;
    }, new Map());

    return (
        <div className='flex flex-col gap-3'>
            {groups.map((group, groupIndex) => (
                <div className='rounded-6 border border-divider p-3' key={group.key}>
                    <div className='flex items-center gap-2'>
                        <Select
                            className='min-w-0 flex-1'
                            disabled={disabled}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            options={dayOptions?.map((option) => ({
                                ...option,
                                disabled:
                                    option.disabled || (option.value !== group.dayOfWeek && usedDays.has(option.value)),
                            }))}
                            size='large'
                            status={
                                showValidation && !dayOptions?.some((option) => option.value === group.dayOfWeek)
                                    ? 'error'
                                    : undefined
                            }
                            value={group.dayOfWeek}
                            onChange={(dayOfWeek) => updateDay(groupIndex, dayOfWeek)}
                        />

                        <Button
                            aria-label={removeDayLabel}
                            className='h-10 w-10 shrink-0'
                            danger
                            disabled={disabled || groups.length === 1}
                            htmlType='button'
                            icon={<DeleteOutlined />}
                            onClick={() => removeDay(groupIndex)}
                            title={removeDayLabel}
                            type='text'
                        />
                    </div>

                    <div className='mt-3'>
                        <div className='grid grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-2'>
                            {group.times.map((timeEntry, timeIndex) => {
                                const isValidTime =
                                    Number.isInteger(timeEntry?.hour) &&
                                    timeEntry.hour >= 0 &&
                                    timeEntry.hour <= 23 &&
                                    Number.isInteger(timeEntry?.minute) &&
                                    timeEntry.minute >= 0 &&
                                    timeEntry.minute <= 59;
                                const timeKey = `${group.dayOfWeek}-${timeEntry.hour}-${timeEntry.minute}`;
                                const isDuplicate = isValidTime && timeCounts.get(timeKey) > 1;
                                const time = isValidTime
                                    ? dayjs().hour(timeEntry.hour).minute(timeEntry.minute).second(0)
                                    : null;

                                return (
                                    <div className='flex min-w-0 items-center gap-1' key={timeEntry.key}>
                                        <TimePicker
                                            allowClear={false}
                                            className='h-10 min-w-0 flex-1 rounded-6 text-14'
                                            disabled={disabled}
                                            format='HH:mm'
                                            minuteStep={1}
                                            placeholder={timePlaceholder}
                                            prefixCls='ep-form-item-timepicker'
                                            status={
                                                showValidation && (!isValidTime || isDuplicate) ? 'error' : undefined
                                            }
                                            value={time}
                                            onChange={(selectedTime) => updateTime(groupIndex, timeIndex, selectedTime)}
                                        />

                                        <Button
                                            aria-label={removeLabel}
                                            className='h-10 w-10 shrink-0'
                                            danger
                                            disabled={disabled || group.times.length === 1}
                                            htmlType='button'
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeTime(groupIndex, timeIndex)}
                                            title={removeLabel}
                                            type='text'
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        <Button
                            className='mt-3 w-full sm:w-auto'
                            disabled={disabled}
                            htmlType='button'
                            icon={<PlusOutlined />}
                            onClick={() => addTime(groupIndex)}
                            type='dashed'
                        >
                            {addLabel}
                        </Button>
                    </div>
                </div>
            ))}

            <Button
                disabled={disabled || !canAddDay}
                htmlType='button'
                icon={<PlusOutlined />}
                onClick={addDay}
                type='dashed'
            >
                {addDayLabel}
            </Button>
        </div>
    );
};

export default PollingScheduleEditor;
